// TODO: Type usage of GeoRBush
import { concat as observableConcat, empty as observableEmpty, from as observableFrom, merge as observableMerge, of as observableOf, Subject, } from "rxjs";
import { catchError, finalize, map, mergeAll, mergeMap, last, publish, publishReplay, reduce, refCount, tap, } from "rxjs/operators";
import { FilterCreator } from "./FilterCreator";
import { GraphCalculator } from "./GraphCalculator";
import { Node } from "./Node";
import { NodeCache } from "./NodeCache";
import { Sequence } from "./Sequence";
import { EdgeCalculator } from "./edge/EdgeCalculator";
import { GraphInfra3DSDKError } from "../error/GraphInfra3DSDKError";
/**
 * @class Graph
 *
 * @classdesc Represents a graph of nodes with edges.
 */
// NOTE: (4)
export class Graph {
    /**
     * Create a new graph instance.
     *
     * @param {APIWrapper} [api] - API instance for retrieving data.
     * @param {rbush.RBush<NodeIndexItem>} [nodeIndex] - Node index for fast spatial retreival.
     * @param {GraphCalculator} [graphCalculator] - Instance for graph calculations.
     * @param {EdgeCalculator} [edgeCalculator] - Instance for edge calculations.
     * @param {FilterCreator} [filterCreator] - Instance for  filter creation.
     * @param {IGraphConfiguration} [configuration] - Configuration struct.
     */
    constructor(api, nodeIndex, graphCalculator, edgeCalculator, filterCreator, configuration) {
        this._api = api;
        this._cachedNodes = {};
        this._cachedNodeTiles = {};
        this._cachedSequenceNodes = {};
        this._cachedSpatialEdges = {};
        this._cachedTiles = {};
        this._cachingFill$ = {};
        this._cachingFull$ = {};
        this._cachingSequenceNodes$ = {};
        this._cachingSequences$ = {};
        this._cachingSpatialArea$ = {};
        this._cachingTiles$ = {};
        this._changed$ = new Subject();
        this._filterCreator = filterCreator != null ? filterCreator : new FilterCreator();
        this._filter = this._filterCreator.createFilter(undefined);
        this._filterSubject$ = new Subject();
        this._filter$ = observableConcat(observableOf(this._filter), this._filterSubject$).pipe(publishReplay(1), refCount());
        this._filterSubscription = this._filter$.subscribe(() => {
            /*noop*/
        });
        this._defaultAlt = 2;
        this._edgeCalculator = edgeCalculator != null ? edgeCalculator : new EdgeCalculator();
        this._graphCalculator = graphCalculator != null ? graphCalculator : new GraphCalculator();
        this._configuration =
            configuration != null
                ? configuration
                : {
                    maxSequences: 50,
                    maxUnusedNodes: 100,
                    maxUnusedPreStoredNodes: 30,
                    maxUnusedTiles: 20,
                };
        this._nodes = {};
        this._nodeIndex = nodeIndex != null ? nodeIndex : new Graph._spatialIndex(16);
        this._nodeIndexTiles = {};
        this._nodeToTile = {};
        this._preStored = {};
        this._requiredNodeTiles = {};
        this._requiredSpatialArea = {};
        this._sequences = {};
        this._tileThreshold = 20;
    }
    static register(spatialIndex) {
        Graph._spatialIndex = spatialIndex;
    }
    /**
     * Get api.
     *
     * @returns {APIWrapper} The API instance used by
     * the graph.
     */
    get api() {
        return this._api;
    }
    /**
     * Get changed$.
     *
     * @returns {Observable<Graph>} Observable emitting
     * the graph every time it has changed.
     */
    get changed$() {
        return this._changed$;
    }
    /**
     * Get filter$.
     *
     * @returns {Observable<FilterFunction>} Observable emitting
     * the filter every time it has changed.
     */
    get filter$() {
        return this._filter$;
    }
    /**
     * Caches the full node data for all images within a bounding
     * box.
     *
     * @description The node assets are not cached.
     *
     * @param {ILatLon} sw - South west corner of bounding box.
     * @param {ILatLon} ne - North east corner of bounding box.
     * @returns {Observable<Array<Node>>} Observable emitting
     * the full nodes in the bounding box.
     */
    cacheBoundingBox$(sw, ne) {
        const cacheTiles$ = this._api.data.geometry
            .bboxToCellIds(sw, ne)
            .filter((h) => {
            return !(h in this._cachedTiles);
        })
            .map((h) => {
            return h in this._cachingTiles$ ? this._cachingTiles$[h] : this._cacheTile$(h);
        });
        if (cacheTiles$.length === 0) {
            cacheTiles$.push(observableOf(this));
        }
        return observableFrom(cacheTiles$).pipe(mergeAll(), last(), mergeMap((graph) => {
            const nodes = this._nodeIndex
                .search({
                maxX: ne.lat,
                maxY: ne.lon,
                minX: sw.lat,
                minY: sw.lon,
            })
                .map((item) => {
                return item.node;
            });
            const fullNodes = [];
            const coreNodes = [];
            for (const node of nodes) {
                if (node.full) {
                    fullNodes.push(node);
                }
                else {
                    coreNodes.push(node.key);
                }
            }
            const coreNodeBatches = [];
            const batchSize = 200;
            while (coreNodes.length > 0) {
                coreNodeBatches.push(coreNodes.splice(0, batchSize));
            }
            const fullNodes$ = observableOf(fullNodes);
            const fillNodes$ = coreNodeBatches.map((batch) => {
                return this._api.imageByKeyFill$(batch).pipe(map((imageByKeyFill) => {
                    const filledNodes = [];
                    for (const fillKey in imageByKeyFill) {
                        if (!Object.prototype.hasOwnProperty.call(imageByKeyFill, fillKey)) {
                            continue;
                        }
                        if (this.hasNode(fillKey)) {
                            const node = this.getNode(fillKey);
                            if (!node.full) {
                                this._makeFull(node, imageByKeyFill[fillKey]);
                            }
                            filledNodes.push(node);
                        }
                    }
                    return filledNodes;
                }));
            });
            return observableMerge(fullNodes$, observableFrom(fillNodes$).pipe(mergeAll()));
        }), reduce((acc, value) => {
            return acc.concat(value);
        }));
    }
    /**
     * Caches the full node data for all images of a cell.
     *
     * @description The node assets are not cached.
     *
     * @param {string} cellId - Cell id.
     * @returns {Observable<Array<Node>>} Observable
     * emitting the full nodes of the cell.
     */
    cacheCell$(cellId) {
        const cacheCell$ = cellId in this._cachedTiles
            ? observableOf(this)
            : cellId in this._cachingTiles$
                ? this._cachingTiles$[cellId]
                : this._cacheTile$(cellId);
        return cacheCell$.pipe(mergeMap(() => {
            const cachedCell = this._cachedTiles[cellId];
            cachedCell.accessed = new Date().getTime();
            const cellNodes = cachedCell.nodes;
            const fullNodes = [];
            const coreNodes = [];
            for (const node of cellNodes) {
                if (node.full) {
                    fullNodes.push(node);
                }
                else {
                    coreNodes.push(node.key);
                }
            }
            const coreNodeBatches = [];
            const batchSize = 200;
            while (coreNodes.length > 0) {
                coreNodeBatches.push(coreNodes.splice(0, batchSize));
            }
            const fullNodes$ = observableOf(fullNodes);
            const fillNodes$ = coreNodeBatches.map((batch) => {
                return this._api.imageByKeyFill$(batch).pipe(map((nodes) => {
                    const filled = [];
                    for (const key in nodes) {
                        if (!Object.prototype.hasOwnProperty.call(nodes, key)) {
                            continue;
                        }
                        if (!this.hasNode(key)) {
                            continue;
                        }
                        const node = this.getNode(key);
                        if (!node.full) {
                            this._makeFull(node, nodes[key]);
                        }
                        filled.push(node);
                    }
                    return filled;
                }));
            });
            return observableMerge(fullNodes$, observableFrom(fillNodes$).pipe(mergeAll()));
        }), reduce((acc, value) => {
            return acc.concat(value);
        }));
    }
    /**
     * Retrieve and cache node fill properties.
     *
     * @param {string} key - Key of node to fill.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the node has been updated.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheFill$(key) {
        if (key in this._cachingFull$) {
            throw new GraphInfra3DSDKError(`Cannot fill node while caching full (${key}).`);
        }
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Cannot fill node that does not exist in graph (${key}).`);
        }
        if (key in this._cachingFill$) {
            return this._cachingFill$[key];
        }
        const node = this.getNode(key);
        if (node.full) {
            throw new GraphInfra3DSDKError(`Cannot fill node that is already full (${key}).`);
        }
        this._cachingFill$[key] = this._api.imageByKeyFill$([key]).pipe(tap((imageByKeyFill) => {
            if (!node.full) {
                this._makeFull(node, imageByKeyFill[key]);
            }
            delete this._cachingFill$[key];
        }), map((imageByKeyFill) => {
            return this;
        }), finalize(() => {
            if (key in this._cachingFill$) {
                delete this._cachingFill$[key];
            }
            this._changed$.next(this);
        }), publish(), refCount());
        return this._cachingFill$[key];
    }
    /**
     * Retrieve and cache full node properties.
     *
     * @param {string} key - Key of node to fill.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the node has been updated.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheFull$(key) {
        if (key in this._cachingFull$) {
            return this._cachingFull$[key];
        }
        if (this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Cannot cache full node that already exist in graph (${key}).`);
        }
        // NOTE: (3)
        this._cachingFull$[key] = this._api.imageByKeyFull$([key]).pipe(tap((imageByKeyFull) => {
            const fn = imageByKeyFull[key];
            if (this.hasNode(key)) {
                const node = this.getNode(key);
                if (!node.full) {
                    this._makeFull(node, fn);
                }
            }
            else {
                if (fn.sequence_key == null) {
                    throw new GraphInfra3DSDKError(`Node has no sequence key (${key}).`);
                }
                const node = new Node(fn);
                this._makeFull(node, fn);
                // NOTE: Review geometry.latLonToCellId using geohash. And for what the prestore is used.
                const h = this._api.data.geometry.latLonToCellId(node.originalLatLon);
                this._preStore(h, node);
                this._setNode(node);
                delete this._cachingFull$[key];
            }
        }), map((imageByKeyFull) => {
            return this;
        }), finalize(() => {
            if (key in this._cachingFull$) {
                delete this._cachingFull$[key];
            }
            this._changed$.next(this);
        }), publish(), refCount());
        return this._cachingFull$[key];
    }
    /**
     * Retrieve and cache a node sequence.
     *
     * @param {string} key - Key of node for which to retrieve sequence.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the sequence has been retrieved.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheNodeSequence$(key) {
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Cannot cache sequence edges of node that does not exist in graph (${key}).`);
        }
        const node = this.getNode(key);
        if (node.sequenceKey in this._sequences) {
            throw new GraphInfra3DSDKError(`Sequence already cached (${key}), (${node.sequenceKey}).`);
        }
        return this._cacheSequence$(node.sequenceKey);
    }
    /**
     * Retrieve and cache a sequence.
     *
     * @param {string} sequenceKey - Key of sequence to cache.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the sequence has been retrieved.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheSequence$(sequenceKey) {
        if (sequenceKey in this._sequences) {
            throw new GraphInfra3DSDKError(`Sequence already cached (${sequenceKey})`);
        }
        return this._cacheSequence$(sequenceKey);
    }
    /**
     * Cache sequence edges for a node.
     *
     * @param {string} key - Key of node.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheSequenceEdges(key) {
        const node = this.getNode(key);
        if (!(node.sequenceKey in this._sequences)) {
            throw new GraphInfra3DSDKError(`Sequence is not cached (${key}), (${node.sequenceKey})`);
        }
        const sequence = this._sequences[node.sequenceKey].sequence;
        const edges = this._edgeCalculator.computeSequenceEdges(node, sequence);
        //debugger;
        node.cacheSequenceEdges(edges);
    }
    /**
     * Retrieve and cache full nodes for all keys in a sequence.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @param {string} referenceNodeKey - Key of node to use as reference
     * for optimized caching.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the nodes of the sequence has been cached.
     */
    cacheSequenceNodes$(sequenceKey, referenceNodeKey) {
        if (!this.hasSequence(sequenceKey)) {
            throw new GraphInfra3DSDKError(`Cannot cache sequence nodes of sequence that does not exist in graph (${sequenceKey}).`);
        }
        if (this.hasSequenceNodes(sequenceKey)) {
            throw new GraphInfra3DSDKError(`Sequence nodes already cached (${sequenceKey}).`);
        }
        const sequence = this.getSequence(sequenceKey);
        if (sequence.key in this._cachingSequenceNodes$) {
            return this._cachingSequenceNodes$[sequence.key];
        }
        const batches = [];
        const keys = sequence.keys.slice();
        const referenceBatchSize = 50;
        if (!!referenceNodeKey && keys.length > referenceBatchSize) {
            const referenceIndex = keys.indexOf(referenceNodeKey);
            const startIndex = Math.max(0, Math.min(referenceIndex - referenceBatchSize / 2, keys.length - referenceBatchSize));
            batches.push(keys.splice(startIndex, referenceBatchSize));
        }
        const batchSize = 200;
        while (keys.length > 0) {
            batches.push(keys.splice(0, batchSize));
        }
        let batchesToCache = batches.length;
        const sequenceNodes$ = observableFrom(batches).pipe(mergeMap((batch) => {
            return this._api.imageByKeyFull$(batch).pipe(tap((imageByKeyFull) => {
                for (const fullKey in imageByKeyFull) {
                    if (!Object.prototype.hasOwnProperty.call(imageByKeyFull, fullKey)) {
                        continue;
                    }
                    const fn = imageByKeyFull[fullKey];
                    if (this.hasNode(fullKey)) {
                        const node = this.getNode(fn.key);
                        if (!node.full) {
                            this._makeFull(node, fn);
                        }
                    }
                    else {
                        if (fn.sequence_key == null) {
                            console.warn(`Sequence missing, discarding node (${fn.key})`);
                        }
                        const node = new Node(fn);
                        this._makeFull(node, fn);
                        const h = this._api.data.geometry.latLonToCellId(node.originalLatLon);
                        this._preStore(h, node);
                        this._setNode(node);
                    }
                }
                batchesToCache--;
            }), map((imageByKeyFull) => {
                return this;
            }));
        }, 6), last(), finalize(() => {
            delete this._cachingSequenceNodes$[sequence.key];
            if (batchesToCache === 0) {
                this._cachedSequenceNodes[sequence.key] = true;
            }
        }), publish(), refCount());
        this._cachingSequenceNodes$[sequence.key] = sequenceNodes$;
        return sequenceNodes$;
    }
    /**
     * Retrieve and cache full nodes for a node spatial area.
     *
     * @param {string} key - Key of node for which to retrieve sequence.
     * @returns {Observable<Graph>} Observable emitting the graph
     * when the nodes in the spatial area has been made full.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheSpatialArea$(key) {
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Cannot cache spatial area of node that does not exist in graph (${key}).`);
        }
        if (key in this._cachedSpatialEdges) {
            throw new GraphInfra3DSDKError(`Node already spatially cached (${key}).`);
        }
        if (!(key in this._requiredSpatialArea)) {
            throw new GraphInfra3DSDKError(`Spatial area not determined (${key}).`);
        }
        const spatialArea = this._requiredSpatialArea[key];
        if (Object.keys(spatialArea.cacheNodes).length === 0) {
            throw new GraphInfra3DSDKError(`Spatial nodes already cached (${key}).`);
        }
        if (key in this._cachingSpatialArea$) {
            return this._cachingSpatialArea$[key];
        }
        const batches = [];
        while (spatialArea.cacheKeys.length > 0) {
            batches.push(spatialArea.cacheKeys.splice(0, 200));
        }
        let batchesToCache = batches.length;
        const spatialNodes$ = [];
        for (const batch of batches) {
            const spatialNodeBatch$ = this._api.imageByKeyFill$(batch).pipe(tap((imageByKeyFill) => {
                for (const fillKey in imageByKeyFill) {
                    if (!Object.prototype.hasOwnProperty.call(imageByKeyFill, fillKey)) {
                        continue;
                    }
                    const spatialNode = spatialArea.cacheNodes[fillKey];
                    if (spatialNode.full) {
                        delete spatialArea.cacheNodes[fillKey];
                        continue;
                    }
                    const fillNode = imageByKeyFill[fillKey];
                    this._makeFull(spatialNode, fillNode);
                    delete spatialArea.cacheNodes[fillKey];
                }
                if (--batchesToCache === 0) {
                    delete this._cachingSpatialArea$[key];
                }
            }), map((imageByKeyFill) => {
                return this;
            }), catchError((error) => {
                for (const batchKey of batch) {
                    if (batchKey in spatialArea.all) {
                        delete spatialArea.all[batchKey];
                    }
                    if (batchKey in spatialArea.cacheNodes) {
                        delete spatialArea.cacheNodes[batchKey];
                    }
                }
                if (--batchesToCache === 0) {
                    delete this._cachingSpatialArea$[key];
                }
                throw error;
            }), finalize(() => {
                if (Object.keys(spatialArea.cacheNodes).length === 0) {
                    this._changed$.next(this);
                }
            }), publish(), refCount());
            spatialNodes$.push(spatialNodeBatch$);
        }
        this._cachingSpatialArea$[key] = spatialNodes$;
        return spatialNodes$;
    }
    /**
     * Cache spatial edges for a node.
     *
     * @param {string} key - Key of node.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheSpatialEdges(key) {
        // NOTE: (3)
        // console.log("cacheSpatialEdges", key);
        if (key in this._cachedSpatialEdges) {
            throw new GraphInfra3DSDKError(`Spatial edges already cached (${key}).`);
        }
        const node = this.getNode(key);
        const sequence = this._sequences[node.sequenceKey].sequence;
        const fallbackKeys = [];
        const prevKey = sequence.findPrevKey(node.key);
        if (prevKey != null) {
            fallbackKeys.push(prevKey);
        }
        const nextKey = sequence.findNextKey(node.key);
        if (nextKey != null) {
            fallbackKeys.push(nextKey);
        }
        const allSpatialNodes = this._requiredSpatialArea[key].all;
        const potentialNodes = [];
        const filter = this._filter;
        for (const spatialNodeKey in allSpatialNodes) {
            if (!Object.prototype.hasOwnProperty.call(allSpatialNodes, spatialNodeKey)) {
                continue;
            }
            const spatialNode = allSpatialNodes[spatialNodeKey];
            if (filter(spatialNode)) {
                potentialNodes.push(spatialNode);
            }
        }
        // NOTE: (3) / REVIEW: If this is correct
        // debugger
        const potentialEdges = this._edgeCalculator.getPotentialEdges(node, potentialNodes, fallbackKeys);
        let edges = this._edgeCalculator.computeStepEdges(node, potentialEdges, prevKey, nextKey, potentialNodes);
        edges = edges.concat(this._edgeCalculator.computeTurnEdges(node, potentialEdges, potentialNodes));
        edges = edges.concat(this._edgeCalculator.computeSphericalEdges(node, potentialEdges, potentialNodes));
        edges = edges.concat(this._edgeCalculator.computePerspectiveToSphericalEdges(node, potentialEdges));
        edges = edges.concat(this._edgeCalculator.computeSimilarEdges(node, potentialEdges));
        node.cacheSpatialEdges(edges);
        this._cachedSpatialEdges[key] = node;
        delete this._requiredSpatialArea[key];
        delete this._cachedNodeTiles[key];
    }
    /**
     * Retrieve and cache tiles for a node.
     *
     * @param {string} key - Key of node for which to retrieve tiles.
     * @returns {Array<Observable<Graph>>} Array of observables emitting
     * the graph for each tile required for the node has been cached.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    cacheTiles$(key) {
        if (key in this._cachedNodeTiles) {
            throw new GraphInfra3DSDKError(`Tiles already cached (${key}).`);
        }
        if (key in this._cachedSpatialEdges) {
            throw new GraphInfra3DSDKError(`Spatial edges already cached so tiles considered cached (${key}).`);
        }
        if (!(key in this._requiredNodeTiles)) {
            throw new GraphInfra3DSDKError(`Tiles have not been determined (${key}).`);
        }
        const nodeTiles = this._requiredNodeTiles[key];
        if (nodeTiles.cache.length === 0 && nodeTiles.caching.length === 0) {
            throw new GraphInfra3DSDKError(`Tiles already cached (${key}).`);
        }
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Cannot cache tiles of node that does not exist in graph (${key}).`);
        }
        const hs = nodeTiles.cache.slice();
        nodeTiles.caching = this._requiredNodeTiles[key].caching.concat(hs);
        nodeTiles.cache = [];
        const cacheTiles$ = [];
        for (const h of nodeTiles.caching) {
            const cacheTile$ = h in this._cachingTiles$ ? this._cachingTiles$[h] : this._cacheTile$(h);
            cacheTiles$.push(cacheTile$.pipe(tap((graph) => {
                const index = nodeTiles.caching.indexOf(h);
                if (index > -1) {
                    nodeTiles.caching.splice(index, 1);
                }
                if (nodeTiles.caching.length === 0 && nodeTiles.cache.length === 0) {
                    delete this._requiredNodeTiles[key];
                    this._cachedNodeTiles[key] = true;
                }
            }), catchError((error) => {
                const index = nodeTiles.caching.indexOf(h);
                if (index > -1) {
                    nodeTiles.caching.splice(index, 1);
                }
                if (nodeTiles.caching.length === 0 && nodeTiles.cache.length === 0) {
                    delete this._requiredNodeTiles[key];
                    this._cachedNodeTiles[key] = true;
                }
                throw error;
            }), finalize(() => {
                this._changed$.next(this);
            }), publish(), refCount()));
        }
        return cacheTiles$;
    }
    /**
     * Initialize the cache for a node.
     *
     * @param {string} key - Key of node.
     * @throws {GraphInfra3DSDKError} When the operation is not valid on the
     * current graph.
     */
    initializeCache(key) {
        if (key in this._cachedNodes) {
            throw new GraphInfra3DSDKError(`Node already in cache (${key}).`);
        }
        const node = this.getNode(key);
        const provider = this._api.data;
        node.initializeCache(new NodeCache(provider));
        const accessed = new Date().getTime();
        this._cachedNodes[key] = { accessed: accessed, node: node };
        this._updateCachedTileAccess(key, accessed);
    }
    /**
     * Get a value indicating if the graph is fill caching a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the node is being fill cached.
     */
    isCachingFill(key) {
        return key in this._cachingFill$;
    }
    /**
     * Get a value indicating if the graph is fully caching a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the node is being fully cached.
     */
    isCachingFull(key) {
        return key in this._cachingFull$;
    }
    /**
     * Get a value indicating if the graph is caching a sequence of a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the sequence of a node is
     * being cached.
     */
    isCachingNodeSequence(key) {
        const node = this.getNode(key);
        return node.sequenceKey in this._cachingSequences$;
    }
    /**
     * Get a value indicating if the graph is caching a sequence.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @returns {boolean} Value indicating if the sequence is
     * being cached.
     */
    isCachingSequence(sequenceKey) {
        return sequenceKey in this._cachingSequences$;
    }
    /**
     * Get a value indicating if the graph is caching sequence nodes.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @returns {boolean} Value indicating if the sequence nodes are
     * being cached.
     */
    isCachingSequenceNodes(sequenceKey) {
        return sequenceKey in this._cachingSequenceNodes$;
    }
    /**
     * Get a value indicating if the graph is caching the tiles
     * required for calculating spatial edges of a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the tiles of
     * a node are being cached.
     */
    isCachingTiles(key) {
        //console.log("isCachingTiles", JSON.stringify(this._requiredNodeTiles));
        const res = key in this._requiredNodeTiles &&
            this._requiredNodeTiles[key].cache.length === 0 &&
            this._requiredNodeTiles[key].caching.length > 0;
        // console.log(res);
        return (key in this._requiredNodeTiles &&
            this._requiredNodeTiles[key].cache.length === 0 &&
            this._requiredNodeTiles[key].caching.length > 0);
    }
    /**
     * Get a value indicating if the cache has been initialized
     * for a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the cache has been
     * initialized for a node.
     */
    hasInitializedCache(key) {
        return key in this._cachedNodes;
    }
    /**
     * Get a value indicating if a node exist in the graph.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if a node exist in the graph.
     */
    hasNode(key) {
        const accessed = new Date().getTime();
        this._updateCachedNodeAccess(key, accessed);
        this._updateCachedTileAccess(key, accessed);
        return key in this._nodes;
    }
    /**
     * Get a value indicating if a node sequence exist in the graph.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if a node sequence exist
     * in the graph.
     */
    hasNodeSequence(key) {
        const node = this.getNode(key);
        const sequenceKey = node.sequenceKey;
        const hasNodeSequence = sequenceKey in this._sequences;
        if (hasNodeSequence) {
            this._sequences[sequenceKey].accessed = new Date().getTime();
        }
        return hasNodeSequence;
    }
    /**
     * Get a value indicating if a sequence exist in the graph.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @returns {boolean} Value indicating if a sequence exist
     * in the graph.
     */
    hasSequence(sequenceKey) {
        const hasSequence = sequenceKey in this._sequences;
        if (hasSequence) {
            this._sequences[sequenceKey].accessed = new Date().getTime();
        }
        return hasSequence;
    }
    /**
     * Get a value indicating if sequence nodes has been cached in the graph.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @returns {boolean} Value indicating if a sequence nodes has been
     * cached in the graph.
     */
    hasSequenceNodes(sequenceKey) {
        return sequenceKey in this._cachedSequenceNodes;
    }
    /**
     * Get a value indicating if the graph has fully cached
     * all nodes in the spatial area of a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the spatial area
     * of a node has been cached.
     */
    hasSpatialArea(key) {
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Spatial area nodes cannot be determined if node not in graph (${key}).`);
        }
        if (key in this._cachedSpatialEdges) {
            return true;
        }
        if (key in this._requiredSpatialArea) {
            return Object.keys(this._requiredSpatialArea[key].cacheNodes).length === 0;
        }
        const node = this.getNode(key);
        const bbox = this._graphCalculator.boundingBoxCorners(node.latLon, this._tileThreshold);
        const spatialItems = this._nodeIndex.search({
            maxX: bbox[1].lat,
            maxY: bbox[1].lon,
            minX: bbox[0].lat,
            minY: bbox[0].lon,
        });
        const spatialNodes = {
            all: {},
            cacheKeys: [],
            cacheNodes: {},
        };
        for (const spatialItem of spatialItems) {
            spatialNodes.all[spatialItem.node.key] = spatialItem.node;
            if (!spatialItem.node.full) {
                spatialNodes.cacheKeys.push(spatialItem.node.key);
                spatialNodes.cacheNodes[spatialItem.node.key] = spatialItem.node;
            }
        }
        this._requiredSpatialArea[key] = spatialNodes;
        return spatialNodes.cacheKeys.length === 0;
    }
    /**
     * Get a value indicating if the graph has a tiles required
     * for a node.
     *
     * @param {string} key - Key of node.
     * @returns {boolean} Value indicating if the the tiles required
     * by a node has been cached.
     */
    hasTiles(key) {
        if (key in this._cachedNodeTiles) {
            return true;
        }
        if (key in this._cachedSpatialEdges) {
            return true;
        }
        if (!this.hasNode(key)) {
            throw new GraphInfra3DSDKError(`Node does not exist in graph (${key}).`);
        }
        let nodeTiles = { cache: [], caching: [] };
        if (!(key in this._requiredNodeTiles)) {
            const node = this.getNode(key);
            nodeTiles.cache = this._api.data.geometry
                .latLonToCellIds(node.latLon, this._tileThreshold)
                .filter((h) => {
                return !(h in this._cachedTiles);
            });
            if (nodeTiles.cache.length > 0) {
                this._requiredNodeTiles[key] = nodeTiles;
            }
        }
        else {
            nodeTiles = this._requiredNodeTiles[key];
        }
        return nodeTiles.cache.length === 0 && nodeTiles.caching.length === 0;
    }
    /**
     * Get a node.
     *
     * @param {string} key - Key of node.
     * @returns {Node} Retrieved node.
     */
    getNode(key) {
        const accessed = new Date().getTime();
        this._updateCachedNodeAccess(key, accessed);
        this._updateCachedTileAccess(key, accessed);
        return this._nodes[key];
    }
    /**
     * Get a sequence.
     *
     * @param {string} sequenceKey - Key of sequence.
     * @returns {Node} Retrieved sequence.
     */
    getSequence(sequenceKey) {
        const sequenceAccess = this._sequences[sequenceKey];
        sequenceAccess.accessed = new Date().getTime();
        return sequenceAccess.sequence;
    }
    /**
     * Reset all spatial edges of the graph nodes.
     */
    resetSpatialEdges() {
        const cachedKeys = Object.keys(this._cachedSpatialEdges);
        for (const cachedKey of cachedKeys) {
            const node = this._cachedSpatialEdges[cachedKey];
            node.resetSpatialEdges();
            delete this._cachedSpatialEdges[cachedKey];
        }
    }
    /**
     * Reset the complete graph but keep the nodes corresponding
     * to the supplied keys. All other nodes will be disposed.
     *
     * @param {Array<string>} keepKeys - Keys for nodes to keep
     * in graph after reset.
     */
    reset(keepKeys) {
        const nodes = [];
        for (const key of keepKeys) {
            if (!this.hasNode(key)) {
                throw new Error(`Node does not exist ${key}`);
            }
            const node = this.getNode(key);
            node.resetSequenceEdges();
            node.resetSpatialEdges();
            nodes.push(node);
        }
        for (const cachedKey of Object.keys(this._cachedNodes)) {
            if (keepKeys.indexOf(cachedKey) !== -1) {
                continue;
            }
            this._cachedNodes[cachedKey].node.dispose();
            delete this._cachedNodes[cachedKey];
        }
        this._cachedNodeTiles = {};
        this._cachedSpatialEdges = {};
        this._cachedTiles = {};
        this._cachingFill$ = {};
        this._cachingFull$ = {};
        this._cachingSequences$ = {};
        this._cachingSpatialArea$ = {};
        this._cachingTiles$ = {};
        this._nodes = {};
        this._nodeToTile = {};
        this._preStored = {};
        for (const node of nodes) {
            this._nodes[node.key] = node;
            const h = this._api.data.geometry.latLonToCellId(node.originalLatLon);
            this._preStore(h, node);
        }
        this._requiredNodeTiles = {};
        this._requiredSpatialArea = {};
        this._sequences = {};
        this._nodeIndexTiles = {};
        this._nodeIndex.clear();
    }
    /**
     * Set the spatial node filter.
     *
     * @emits FilterFunction The filter function to the Graph#filter$
     * observable.
     *
     * @param {FilterExpression} filter - Filter expression to be applied
     * when calculating spatial edges.
     */
    setFilter(filter) {
        this._filter = this._filterCreator.createFilter(filter);
        this._filterSubject$.next(this._filter);
    }
    /**
     * Uncache the graph according to the graph configuration.
     *
     * @description Uncaches unused tiles, unused nodes and
     * sequences according to the numbers specified in the
     * graph configuration. Sequences does not have a direct
     * reference to either tiles or nodes and may be uncached
     * even if they are related to the nodes that should be kept.
     *
     * @param {Array<string>} keepKeys - Keys of nodes to keep in
     * graph unrelated to last access. Tiles related to those keys
     * will also be kept in graph.
     * @param {string} keepSequenceKey - Optional key of sequence
     * for which the belonging nodes should not be disposed or
     * removed from the graph. These nodes may still be uncached if
     * not specified in keep keys param.
     */
    uncache(keepKeys, keepSequenceKey) {
        const keysInUse = {};
        this._addNewKeys(keysInUse, this._cachingFull$);
        this._addNewKeys(keysInUse, this._cachingFill$);
        this._addNewKeys(keysInUse, this._cachingSpatialArea$);
        this._addNewKeys(keysInUse, this._requiredNodeTiles);
        this._addNewKeys(keysInUse, this._requiredSpatialArea);
        for (const key of keepKeys) {
            if (key in keysInUse) {
                continue;
            }
            keysInUse[key] = true;
        }
        const keepHs = {};
        for (const key in keysInUse) {
            if (!Object.prototype.hasOwnProperty.call(keysInUse, key)) {
                continue;
            }
            const node = this._nodes[key];
            const nodeHs = this._api.data.geometry.latLonToCellIds(node.latLon, this._tileThreshold);
            for (const nodeH of nodeHs) {
                if (!(nodeH in keepHs)) {
                    keepHs[nodeH] = true;
                }
            }
        }
        const potentialHs = [];
        for (const h in this._cachedTiles) {
            if (!Object.prototype.hasOwnProperty.call(this._cachedTiles, h) || h in keepHs) {
                continue;
            }
            potentialHs.push([h, this._cachedTiles[h]]);
        }
        const uncacheHs = potentialHs
            .sort((h1, h2) => {
            return h2[1].accessed - h1[1].accessed;
        })
            .slice(this._configuration.maxUnusedTiles)
            .map((h) => {
            return h[0];
        });
        for (const uncacheH of uncacheHs) {
            this._uncacheTile(uncacheH, keepSequenceKey);
        }
        const potentialPreStored = [];
        const nonCachedPreStored = [];
        for (const h in this._preStored) {
            if (!Object.prototype.hasOwnProperty.call(this._preStored, h) || h in this._cachingTiles$) {
                continue;
            }
            const prestoredNodes = this._preStored[h];
            for (const key in prestoredNodes) {
                if (!Object.prototype.hasOwnProperty.call(prestoredNodes, key) || key in keysInUse) {
                    continue;
                }
                if (prestoredNodes[key].sequenceKey === keepSequenceKey) {
                    continue;
                }
                if (key in this._cachedNodes) {
                    potentialPreStored.push([this._cachedNodes[key], h]);
                }
                else {
                    nonCachedPreStored.push([key, h]);
                }
            }
        }
        const uncachePreStored = potentialPreStored
            .sort(([na1, h1], [na2, h2]) => {
            return na2.accessed - na1.accessed;
        })
            .slice(this._configuration.maxUnusedPreStoredNodes)
            .map(([na, h]) => {
            return [na.node.key, h];
        });
        this._uncachePreStored(nonCachedPreStored);
        this._uncachePreStored(uncachePreStored);
        const potentialNodes = [];
        for (const key in this._cachedNodes) {
            if (!Object.prototype.hasOwnProperty.call(this._cachedNodes, key) || key in keysInUse) {
                continue;
            }
            potentialNodes.push(this._cachedNodes[key]);
        }
        const uncacheNodes = potentialNodes
            .sort((n1, n2) => {
            return n2.accessed - n1.accessed;
        })
            .slice(this._configuration.maxUnusedNodes);
        for (const nodeAccess of uncacheNodes) {
            nodeAccess.node.uncache();
            const key = nodeAccess.node.key;
            delete this._cachedNodes[key];
            if (key in this._cachedNodeTiles) {
                delete this._cachedNodeTiles[key];
            }
            if (key in this._cachedSpatialEdges) {
                delete this._cachedSpatialEdges[key];
            }
        }
        const potentialSequences = [];
        for (const sequenceKey in this._sequences) {
            if (!Object.prototype.hasOwnProperty.call(this._sequences, sequenceKey) ||
                sequenceKey in this._cachingSequences$ ||
                sequenceKey === keepSequenceKey) {
                continue;
            }
            potentialSequences.push(this._sequences[sequenceKey]);
        }
        const uncacheSequences = potentialSequences
            .sort((s1, s2) => {
            return s2.accessed - s1.accessed;
        })
            .slice(this._configuration.maxSequences);
        for (const sequenceAccess of uncacheSequences) {
            const sequenceKey = sequenceAccess.sequence.key;
            delete this._sequences[sequenceKey];
            if (sequenceKey in this._cachedSequenceNodes) {
                delete this._cachedSequenceNodes[sequenceKey];
            }
            sequenceAccess.sequence.dispose();
        }
    }
    /**
     * Updates existing cells with new core nodes.
     *
     * @description Non-existing cells are discarded
     * and not requested at all.
     *
     * Existing nodes are not changed.
     *
     * New nodes are not made full or getting assets
     * cached.
     *
     * @param {Array<string>} cellIds - Cell ids.
     * @returns {Observable<Array<Node>>} Observable
     * emitting the updated cells.
     */
    updateCells$(cellIds) {
        const cachedCells = this._cachedTiles;
        const cachingCells = this._cachingTiles$;
        return observableFrom(cellIds).pipe(mergeMap((cellId) => {
            if (cellId in cachedCells) {
                return this._updateCell$(cellId);
            }
            if (cellId in cachingCells) {
                return cachingCells[cellId].pipe(catchError(() => {
                    return observableOf(this);
                }), mergeMap(() => this._updateCell$(cellId)));
            }
            return observableEmpty();
        }));
    }
    /**
     * Unsubscribes all subscriptions.
     *
     * @description Afterwards, you must not call any other methods
     * on the graph instance.
     */
    unsubscribe() {
        this._filterSubscription.unsubscribe();
    }
    _addNewKeys(keys, dict) {
        for (const key in dict) {
            if (!Object.prototype.hasOwnProperty.call(dict, key) || !this.hasNode(key)) {
                continue;
            }
            if (!(key in keys)) {
                keys[key] = true;
            }
        }
    }
    _cacheSequence$(sequenceKey) {
        if (sequenceKey in this._cachingSequences$) {
            return this._cachingSequences$[sequenceKey];
        }
        // NOTE: (3)
        this._cachingSequences$[sequenceKey] = this._api.sequenceByKey$([sequenceKey]).pipe(tap((sequenceByKey) => {
            if (!(sequenceKey in this._sequences)) {
                this._sequences[sequenceKey] = {
                    accessed: new Date().getTime(),
                    sequence: new Sequence(sequenceByKey[sequenceKey]),
                };
            }
            delete this._cachingSequences$[sequenceKey];
        }), map((sequenceByKey) => {
            return this;
        }), finalize(() => {
            if (sequenceKey in this._cachingSequences$) {
                delete this._cachingSequences$[sequenceKey];
            }
            this._changed$.next(this);
        }), publish(), refCount());
        return this._cachingSequences$[sequenceKey];
    }
    _cacheTile$(h) {
        this._cachingTiles$[h] = this._api.imagesByH$(h).pipe(tap((imagesByH) => {
            const coreNodes = imagesByH[h];
            if (h in this._cachedTiles) {
                return;
            }
            this._nodeIndexTiles[h] = [];
            this._cachedTiles[h] = {
                accessed: new Date().getTime(),
                nodes: [],
            };
            const hCache = this._cachedTiles[h].nodes;
            const preStored = this._removeFromPreStore(h);
            for (const index in coreNodes) {
                if (!Object.prototype.hasOwnProperty.call(coreNodes, index)) {
                    continue;
                }
                const coreNode = coreNodes[index];
                if (coreNode == null) {
                    break;
                }
                if (coreNode.sequence_key == null) {
                    console.warn(`Sequence missing, discarding node (${coreNode.key})`);
                    continue;
                }
                if (preStored != null && coreNode.key in preStored) {
                    const preStoredNode = preStored[coreNode.key];
                    delete preStored[coreNode.key];
                    hCache.push(preStoredNode);
                    const preStoredNodeIndexItem = {
                        lat: preStoredNode.latLon.lat,
                        lon: preStoredNode.latLon.lon,
                        node: preStoredNode,
                    };
                    this._nodeIndex.insert(preStoredNodeIndexItem);
                    this._nodeIndexTiles[h].push(preStoredNodeIndexItem);
                    this._nodeToTile[preStoredNode.key] = h;
                    continue;
                }
                const node = new Node(coreNode);
                hCache.push(node);
                const nodeIndexItem = {
                    lat: node.latLon.lat,
                    lon: node.latLon.lon,
                    node: node,
                };
                this._nodeIndex.insert(nodeIndexItem);
                this._nodeIndexTiles[h].push(nodeIndexItem);
                this._nodeToTile[node.key] = h;
                this._setNode(node);
            }
            delete this._cachingTiles$[h];
        }), map((imagesByH) => {
            return this;
        }), catchError((error) => {
            delete this._cachingTiles$[h];
            throw error;
        }), publish(), refCount());
        return this._cachingTiles$[h];
    }
    _makeFull(node, fillNode) {
        if (fillNode.calt == null) {
            if (fillNode.frame.height) {
                fillNode.calt = fillNode.frame.height;
            }
            else {
                fillNode.calt = this._defaultAlt;
            }
        }
        // NOTE: (3)
        if (fillNode.c_rotation == null) {
            fillNode.c_rotation = this._graphCalculator.rotationFromCompass(fillNode.ca, fillNode.orientation);
        }
        node.makeFull(fillNode);
    }
    _preStore(h, node) {
        if (!(h in this._preStored)) {
            this._preStored[h] = {};
        }
        this._preStored[h][node.key] = node;
    }
    _removeFromPreStore(h) {
        let preStored = null;
        if (h in this._preStored) {
            preStored = this._preStored[h];
            delete this._preStored[h];
        }
        return preStored;
    }
    _setNode(node) {
        const key = node.key;
        if (this.hasNode(key)) {
            console.warn(`Node already exist (${key}).`);
            return;
        }
        this._nodes[key] = node;
    }
    _uncacheTile(h, keepSequenceKey) {
        for (const node of this._cachedTiles[h].nodes) {
            const key = node.key;
            delete this._nodeToTile[key];
            if (key in this._cachedNodes) {
                delete this._cachedNodes[key];
            }
            if (key in this._cachedNodeTiles) {
                delete this._cachedNodeTiles[key];
            }
            if (key in this._cachedSpatialEdges) {
                delete this._cachedSpatialEdges[key];
            }
            if (node.sequenceKey === keepSequenceKey) {
                this._preStore(h, node);
                node.uncache();
            }
            else {
                delete this._nodes[key];
                if (node.sequenceKey in this._cachedSequenceNodes) {
                    delete this._cachedSequenceNodes[node.sequenceKey];
                }
                node.dispose();
            }
        }
        for (const nodeIndexItem of this._nodeIndexTiles[h]) {
            this._nodeIndex.remove(nodeIndexItem);
        }
        delete this._nodeIndexTiles[h];
        delete this._cachedTiles[h];
    }
    _uncachePreStored(preStored) {
        const hs = {};
        for (const [key, h] of preStored) {
            if (key in this._nodes) {
                delete this._nodes[key];
            }
            if (key in this._cachedNodes) {
                delete this._cachedNodes[key];
            }
            const node = this._preStored[h][key];
            if (node.sequenceKey in this._cachedSequenceNodes) {
                delete this._cachedSequenceNodes[node.sequenceKey];
            }
            delete this._preStored[h][key];
            node.dispose();
            hs[h] = true;
        }
        for (const h in hs) {
            if (!Object.prototype.hasOwnProperty.call(hs, h)) {
                continue;
            }
            if (Object.keys(this._preStored[h]).length === 0) {
                delete this._preStored[h];
            }
        }
    }
    _updateCachedTileAccess(key, accessed) {
        if (key in this._nodeToTile) {
            this._cachedTiles[this._nodeToTile[key]].accessed = accessed;
        }
    }
    _updateCachedNodeAccess(key, accessed) {
        if (key in this._cachedNodes) {
            this._cachedNodes[key].accessed = accessed;
        }
    }
    _updateCell$(cellId) {
        return this._api.imagesByH$(cellId).pipe(mergeMap((imagesByH) => {
            if (!(cellId in this._cachedTiles)) {
                return observableEmpty();
            }
            const nodeIndex = this._nodeIndex;
            const nodeIndexCell = this._nodeIndexTiles[cellId];
            const nodeToCell = this._nodeToTile;
            const cell = this._cachedTiles[cellId];
            cell.accessed = new Date().getTime();
            const cellNodes = cell.nodes;
            const coreNodes = imagesByH[cellId];
            for (const index in coreNodes) {
                if (!Object.prototype.hasOwnProperty.call(coreNodes, index)) {
                    continue;
                }
                const coreNode = coreNodes[index];
                if (coreNode == null) {
                    break;
                }
                if (this.hasNode(coreNode.key)) {
                    continue;
                }
                if (coreNode.sequence_key == null) {
                    console.warn(`Sequence missing, discarding node ` + `(${coreNode.key})`);
                    continue;
                }
                const node = new Node(coreNode);
                cellNodes.push(node);
                const nodeIndexItem = {
                    lat: node.latLon.lat,
                    lon: node.latLon.lon,
                    node: node,
                };
                nodeIndex.insert(nodeIndexItem);
                nodeIndexCell.push(nodeIndexItem);
                nodeToCell[node.key] = cellId;
                this._setNode(node);
            }
            return observableOf(cellId);
        }), catchError((error) => {
            console.error(error);
            return observableEmpty();
        }));
    }
}
//# sourceMappingURL=Graph.js.map